/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { toast } from "react-toastify";
import api from "../../services/api";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import IconButton from "../../components/ui/IconButton";
import LoadingButton from "../../components/ui/LoadingButton";
import Radio from "../../components/ui/Radio";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import QrCode2Icon from "@mui/icons-material/QrCode2";

import ConfirmDialog from "../../components/ConfirmDialog";
import { useIntl } from "../../contexts/IntlContext";
import { PageHeader } from "../../components/PageHeader";
import { TableEmptyState } from "../../components/table/TableEmptyState";
import { TableSkeletonRows } from "../../components/table/TableSkeletonRows";
import { StandardTablePagination } from "../../components/table/StandardTablePagination";
import { EstablishmentAutocomplete } from "../../components/ui/EstablishmentAutocomplete";
import QrCodeDialog from "../../components/QrCodeDialog";
import CreateLinkDialog from "../Establishment/EstablishmentList/CreateLinkDialog/CreateLinkDialog";
import { Establishment } from "../../types/establishment";

interface Column {
  id: "selected" | "establishmentName" | "fullAddress" | "createdAt";
  label: string;
  minWidth?: number;
  align?: "right";
}

interface Data {
  hashUrl: string;
  fullAddress: string;
  selected: boolean;
  establishmentName: string;
  createdAt: string;
}

export default function EstablishmentHashList() {
  const intl = useIntl();
  const [hashes, setHashes] = useState<Data[]>([]);

  const columns: readonly Column[] = [
    {
      id: "selected",
      label: "",
      minWidth: 10,
    },
    {
      id: "establishmentName",
      label: intl.formatMessage({ id: "common.establishmentName" }),
      minWidth: 150,
    },
    {
      id: "fullAddress",
      label: intl.formatMessage({ id: "establishmentHashList.address" }),
      minWidth: 150,
    },
    {
      id: "createdAt",
      label: intl.formatMessage({ id: "establishmentList.createdAt" }),
      minWidth: 100,
    },
  ];

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [establishmentPick, setEstablishmentPick] = useState<Establishment[]>([]);
  const [createLinkOpen, setCreateLinkOpen] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  const selectedHash = useMemo(() => {
    const selected = hashes.find((hash) => hash.selected);
    return selected!;
  }, [hashes]);

  useEffect(() => {
    setQrOpen(false);
  }, [selectedHash?.hashUrl]);

  const pagedHashes = useMemo(
    () => hashes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [hashes, page, rowsPerPage]
  );

  const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
    const newEstablishmentHashes = hashes.map((hash) => {
      if (hash.hashUrl === id) {
        return { ...hash, selected: !hash.selected };
      }
      return {
        ...hash,
        selected: false,
      };
    });

    setHashes(newEstablishmentHashes);
  };

  const getEstablishmentHashes = useCallback(async () => {
    setListLoading(true);
    try {
      const response = await api.get("/app/establishment-hashes");

      const data = response.data.map((hash: Data) => {
        return {
          ...hash,
          selected: false,
        };
      });

      setHashes(data);
    } catch (error) {
      toast.error(intl.formatMessage({ id: "establishmentHashList.fetchError" }));
    } finally {
      setListLoading(false);
    }
  }, [intl]);

  useEffect(() => {
    getEstablishmentHashes();
  }, []);

  const wasCreateLinkOpenRef = React.useRef(false);
  useEffect(() => {
    if (wasCreateLinkOpenRef.current && !createLinkOpen) {
      void getEstablishmentHashes();
    }
    wasCreateLinkOpenRef.current = createLinkOpen;
  }, [createLinkOpen, getEstablishmentHashes]);

  async function handleDeleteEstablishmentHash() {
    try {
      await api.delete(`app/establishment-hashes/${selectedHash!.hashUrl}`);

      const newEstablishmentHashes = hashes.filter(
        (hash) => hash.hashUrl !== selectedHash!.hashUrl
      );
      setHashes(newEstablishmentHashes);
      if (page > 0 && Math.ceil(newEstablishmentHashes.length / rowsPerPage) <= page) {
        setPage(Math.max(0, page - 1));
      }
      setDeleteDialogOpen(false);
      toast.success(intl.formatMessage({ id: "establishmentHashList.deleteSuccess" }));
    } catch (error) {
      // @ts-ignore
      if (error?.response?.status === 400 || error?.response?.status === 401) {
        // @ts-ignore
        toast.error(error.response.data.message);
      } else {
        toast.error(intl.formatMessage({ id: "messages.connectionError" }));
      }
    }
  }
  return (
    <>
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <PageHeader
          title={intl.formatMessage({ id: "establishmentHashList.title" })}
          selectedLabel={selectedHash?.establishmentName}
          isSelectionActive={!!selectedHash}
          loading={listLoading}
          sx={{ px: 2, py: 1 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LoadingButton
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setEstablishmentPick([]);
                setPickerOpen(true);
              }}
            >
              {intl.formatMessage({ id: "establishmentHashList.createLink" })}
            </LoadingButton>
            {selectedHash && (
              <IconButton
                tooltip={intl.formatMessage({ id: "establishmentHashList.qrCode" })}
                onClick={() => setQrOpen(true)}
              >
                <QrCode2Icon />
              </IconButton>
            )}
            {selectedHash && (
              <IconButton
                tooltip={intl.formatMessage({ id: "common.delete" })}
                onClick={() => setDeleteDialogOpen(true)}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        </PageHeader>

        <TableContainer sx={{ maxHeight: '65vh' }}>
          <Table stickyHeader size="small" aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {listLoading ? (
                <TableSkeletonRows
                  rows={rowsPerPage > 10 ? 10 : rowsPerPage}
                  columns={columns.length}
                />
              ) : hashes.length === 0 ? (
                <TableEmptyState colSpan={columns.length}>
                  {intl.formatMessage({ id: "establishmentHashList.empty" })}
                </TableEmptyState>
              ) : (
                pagedHashes.map((row) => {
                  return (
                    <TableRow
                      hover
                      role="radio"
                      tabIndex={-1}
                      selected={row.selected}
                      aria-checked={row.selected}
                      onClick={(event) => handleClick(event, row.hashUrl)}
                      sx={{ cursor: "pointer" }}
                      key={row.hashUrl}
                    >
                      {columns.map((column) => {
                        const value = row[column.id];

                        if (column.id === "selected") {
                          return (
                            <TableCell padding="checkbox" key={column.id}>
                              <Radio
                                color="primary"
                                checked={row.selected}
                                inputProps={{
                                  "aria-labelledby": row.hashUrl,
                                }}
                              />
                            </TableCell>
                          );
                        }

                        return (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            sx={{ wordBreak: "break-word" }}
                          >
                            {value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {hashes.length > 0 && (
          <StandardTablePagination
            count={hashes.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(+e.target.value);
              setPage(0);
            }}
          />
        )}
      </Paper>
      <ConfirmDialog
        open={deleteDialogOpen}
        title={intl.formatMessage({ id: "common.confirm" })}
        message={intl.formatMessage({ id: "deleteDialog.confirmMessage" })}
        confirmLabel={intl.formatMessage({ id: "common.yes" })}
        cancelLabel={intl.formatMessage({ id: "common.no" })}
        onConfirm={handleDeleteEstablishmentHash}
        onClose={() => setDeleteDialogOpen(false)}
      />

      <QrCodeDialog
        open={qrOpen && !!selectedHash}
        onClose={() => setQrOpen(false)}
        url={selectedHash?.fullAddress ?? ""}
        title={selectedHash?.establishmentName}
      />

      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{intl.formatMessage({ id: "establishmentHashList.createLink" })}</DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <EstablishmentAutocomplete
            value={establishmentPick}
            onChange={(items) => {
              const last = items[items.length - 1];
              setEstablishmentPick(last ? [last] : []);
              if (last) {
                setPickerOpen(false);
                setCreateLinkOpen(true);
              }
            }}
          />
        </DialogContent>
      </Dialog>

      {establishmentPick[0] && (
        <CreateLinkDialog
          open={createLinkOpen}
          setOpen={setCreateLinkOpen}
          establishment={{
            name: establishmentPick[0].name,
            state: establishmentPick[0].state ?? "",
            city: establishmentPick[0].city ?? "",
          }}
        />
      )}
    </>
  );
}
